import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { useStore } from 'store/useStore'
import { OrderKeys } from 'sections/LKSection/modules/OrderModule/data'
import { Margin } from 'components/ui'
import { Href } from 'components/ui/Href/Href'
import { ListItem } from 'components/ui/ListItem/ListItem'
import { Button, Checkbox, Divider, Flex, Paragraph, Text, Title } from 'components/uiKit'
import { getBrandbookPath } from 'utils/helpers/getBrandbookPath'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { sendEvent } from 'utils/helpers/sendEvent'
import { docs } from 'shared/constants/docs'
import { URLS } from 'shared/constants/urls'
import { colors } from 'shared/constants/values'
import { Country } from 'shared/types/types'
import { EventType } from 'types/types'

import s from './RequirementsStep.module.scss'

export const RequirementsStep = observer(() => {
  const { createOrderUiStore: uiStore, appStore } = useStore()

  const { t } = useTranslation()

  const country = appStore.companyCountry

  const getKey = (key: string) => `${OrderKeys.requirementsStep}.${key}`

  const nextStepHandler = () => {
    sendEvent(EventType.orderStep, { step: 1, success: 1 })
    uiStore.setSelectedStep(uiStore.selectedStep + 1)
  }

  return (
    <div className={s.RequirementsStep}>
      <Title level={3}>{t(getKey('Знакомство с требованиями'))}</Title>

      <Margin mt={24}>
        <Flex vertical>
          <Text>{t(getKey('Внимательно изучите изложенные'))}</Text>
          <Text>{t(getKey('Если ваше помещение соответствует'))}</Text>
        </Flex>
      </Margin>

      <Divider />

      <Title level={4}>{t(getKey('Общие требования к партнёрскому'))}</Title>

      <ul className={s.ul}>
        <li className={s.li}>
          <Text style={stylesForMarker}>1.</Text>
          <Text>{t(getKey('Помещение должно быть нежилым'))}</Text>
        </li>
        <li className={s.li}>
          <Text style={stylesForMarker}>2.</Text>
          <div>
            {country !== Country.GE ? (
              <>
                <Text>{t(getKey('Общая площадь помещения'))}</Text>
                <Paragraph>{t(getKey('Для РФ в городах'))}</Paragraph>
                <Paragraph>{t(getKey('Для РФ в малонаселенных'))}</Paragraph>
                <Paragraph>{t(getKey('Для стран СНГ'))}</Paragraph>
                <Paragraph>{t(getKey('В Армении, Казахстане и Беларуси'))}</Paragraph>
                <Text>{t(getKey('В Киргизии и Узбекистане'))}</Text>
              </>
            ) : (
              <Text>{t(getKey('Общая площадь помещения ПВЗ'))}</Text>
            )}
          </div>
        </li>
        <li className={s.li}>
          <Text style={stylesForMarker}>3.</Text>
          <Text>{t(getKey('У ПВЗ должна быть'))}</Text>
        </li>
        <li className={s.li}>
          <Text style={stylesForMarker}>4.</Text>
          <Text>{t(getKey('Не допускается активация'))}</Text>
        </li>
        <li className={s.li}>
          <Text style={stylesForMarker}>5.</Text>
          <Text>{t(getKey('Необходимо наличие ночного'))}</Text>
        </li>
        <li className={s.li}>
          <Text style={stylesForMarker}>6.</Text>
          <div>
            {country !== Country.GE ? (
              <Text>{t(getKey('Должен быть выполнен качественный'))}</Text>
            ) : (
              <Text>{t(getKey('Качественный ремонт'))}</Text>
            )}{' '}
            <Href href={country === Country.GE ? getBrandbookPath() : docs.brandbook} target="_blank">
              {t(getKey('брендбуку'))}
            </Href>
            {country !== Country.GE && (
              <p>
                <Text>{t(getKey('Помощь с планировкой'))}</Text>
              </p>
            )}
          </div>
        </li>
        {country !== Country.GE && (
          <>
            <li className={s.li}>
              <Text style={stylesForMarker}>7.</Text>
              <div>
                <Text>{t(getKey('Минимальный набор оборудования'))}</Text>
                <Margin mt={12}>
                  <ul className={s.ul}>
                    <ListItem>
                      <Text>{t(getKey('Световой режимник'))}</Text>
                    </ListItem>
                    <ListItem>
                      <Text>{t(getKey('Вывеска и кронштейн'))}</Text>
                    </ListItem>
                    <ListItem>
                      <Text>{t(getKey('Стеллажи на складе'))}</Text>
                    </ListItem>
                    <ListItem>
                      <Text>{t(getKey('2 примерочные'))}</Text>
                    </ListItem>
                    <ListItem>
                      <Text>{t(getKey('Ресепшен и стол'))}</Text>
                    </ListItem>
                    <ListItem>
                      <Text>
                        {t(getKey('Камеры видеонаблюдения'))}{' '}
                        <Text style={{ ...stylesForMarker, display: 'inline' }}>*</Text>
                      </Text>
                    </ListItem>
                    <ListItem>
                      <Text>{t(getKey('Электронный замок'))}</Text>
                    </ListItem>
                  </ul>
                </Margin>
              </div>
            </li>
            <li className={s.li}>
              <Text style={stylesForMarker}>7.1.</Text>
              <div>
                <Text>{t(getKey('Оборудование ПВЗ'))}</Text>
                <Margin mt={12}>
                  <ul className={s.ul}>
                    <ListItem>
                      <Text>{t(getKey('Открытие бизнеса'))}</Text>
                    </ListItem>
                    <ListItem>
                      <Text>{t(getKey('Площадь ПВЗ'))}</Text>
                    </ListItem>
                    <ListItem>
                      <Text>{t(getKey('Стол для заказов'))}</Text>
                    </ListItem>
                    <ListItem>
                      <Text>{t(getKey('Примерочные необязательны'))}</Text>
                    </ListItem>
                    <ListItem>
                      <Text>
                        {t(getKey('Одна камера'))} <Text style={{ ...stylesForMarker, display: 'inline' }}>*</Text>
                      </Text>
                    </ListItem>
                    <ListItem>
                      <Text>{t(getKey('Смартфон или ноутбук'))}</Text>
                    </ListItem>
                    <ListItem>
                      <Text>{t(getKey('Мини вывеска'))}</Text>
                    </ListItem>
                    <ListItem>
                      <Text>{t(getKey('Передача ключа'))}</Text>
                    </ListItem>
                  </ul>
                  <Flex vertical gap={16}>
                    <Text type="secondary" style={{ fontSize: 14, paddingTop: 20 }}>
                      <Text style={{ ...stylesForMarker, display: 'inline' }}>*</Text>{' '}
                      {t(getKey('Необходимо установить 2'))}
                    </Text>
                  </Flex>
                </Margin>
              </div>
            </li>
          </>
        )}
      </ul>

      <Divider dashed />

      {country !== Country.GE && (
        <Flex vertical gap={16}>
          <Text type="secondary" style={{ fontSize: 14 }}>
            <Text style={{ ...stylesForMarker, display: 'inline' }}>*</Text> {t(getKey('Согласно п. 6.1 и 6.6'))}
          </Text>
        </Flex>
      )}

      <Margin my={24}>
        <Title level={5}>{t(getKey('Рекомендуем также ознакомиться'))}</Title>
      </Margin>

      <ul className={s.ul}>
        <ListItem>
          <Href href={country === Country.GE ? URLS.offerGE : URLS.offer} target="_blank">
            {t(getKey('Оферта об оказании'))}
          </Href>
        </ListItem>
        <ListItem>
          <Href href={country === Country.GE ? URLS.applicationGe : URLS.application} target="_blank">
            {t(getKey('Правила оказания'))}
          </Href>
        </ListItem>
      </ul>

      <Margin my={24}>
        <Checkbox
          checked={uiStore.agreedWithRequirements}
          onChange={(e) => uiStore.setAgreedWithRequirements(e.target.checked)}
          data-testid="agreedWithRequirements"
        >
          {t(getKey('Я ознакомлен(а) и согласен(на)'))}
        </Checkbox>
      </Margin>

      <Button
        onClick={nextStepHandler}
        disabled={!uiStore.agreedWithRequirements}
        data-testid="nextStepAfterRequirements"
      >
        {t(getSharedKey('next'))}
      </Button>
    </div>
  )
})

const stylesForMarker = {
  color: colors.corp,
  fontWeight: 600,
  display: 'block',
  minWidth: 25,
}
